import { CsProcessSpec, CsProcessNode, CsProcessEdge } from '../cs-chat-segments';

/** A node with computed geometry (top-left x/y + size + centre). */
export interface PositionedNode extends CsProcessNode {
	x: number; y: number; w: number; h: number; cx: number; cy: number;
	/** SVG polygon points for a decision diamond (only set when kind === 'decision'). */
	points?: string;
}

/** An edge with a computed SVG path + a point to hang its label on. */
export interface PositionedEdge extends CsProcessEdge {
	path: string; labelX: number; labelY: number; back: boolean;
}

export interface ProcessLayout {
	width: number; height: number;
	nodes: PositionedNode[];
	edges: PositionedEdge[];
}

const PAD       = 18;   // canvas padding
const RANK_GAP  = 60;   // gap between ranks (along the flow direction)
const ORDER_GAP = 26;   // gap between siblings within a rank

function nodeSize(n: CsProcessNode): { w: number; h: number } {
	if (n.kind === 'decision') { return { w: 150, h: 92 }; }
	return { w: 172, h: n.metric ? 62 : 50 };
}

const round = (n: number) => Math.round(n * 10) / 10;

/** Point on a cubic Bézier at parameter t (for placing edge labels at the midpoint). */
function bezier(t: number, p0: number[], p1: number[], p2: number[], p3: number[]): number[] {
	const u = 1 - t, tt = t * t, uu = u * u;
	const a = uu * u, b = 3 * uu * t, c = 3 * u * tt, d = tt * t;
	return [a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
	        a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]];
}

/**
 * Layered ("Sugiyama-lite") layout for a process flow. Ranks nodes by longest path from the sources
 * (so the flow reads left→right or top→bottom), orders siblings within each rank by the barycentre of
 * their neighbours to reduce edge crossings, then centres each rank and routes edges as smooth Béziers.
 * Cycles are handled by detecting back-edges (DFS) and excluding them from ranking — they're still
 * drawn (dashed), just not allowed to create an infinite rank.
 */
export function layoutProcess(spec: CsProcessSpec): ProcessLayout {
	const dir   = spec.direction === 'TB' ? 'TB' : 'LR';
	const nodes = spec.nodes;
	const index = new Map<string, number>(nodes.map((n, i): [string, number] => [n.id, i]));
	const edges = spec.edges.filter(e => index.has(e.from) && index.has(e.to));

	// ── break cycles: mark back-edges via DFS so ranking sees a DAG ──────────────
	const back = new Set<CsProcessEdge>();
	{
		const adj = new Map<string, CsProcessEdge[]>();
		nodes.forEach(n => adj.set(n.id, []));
		edges.forEach(e => adj.get(e.from)!.push(e));
		const state = new Map<string, number>();   // 0 unvisited, 1 in-stack, 2 done
		nodes.forEach(n => state.set(n.id, 0));
		const visit = (root: string) => {
			// iterative DFS to avoid deep recursion
			const it: string[] = [root];
			const childIdx = new Map<string, number>();
			while (it.length) {
				const u = it[it.length - 1];
				if (state.get(u) === 0) { state.set(u, 1); childIdx.set(u, 0); }
				const es = adj.get(u)!;
				let advanced = false;
				for (let i = childIdx.get(u)!; i < es.length; i++) {
					const e = es[i];
					const s = state.get(e.to)!;
					if (s === 1)      { back.add(e); }
					else if (s === 0) { childIdx.set(u, i + 1); it.push(e.to); advanced = true; break; }
					childIdx.set(u, i + 1);
				}
				if (!advanced && it[it.length - 1] === u) { state.set(u, 2); it.pop(); }
			}
		};
		nodes.forEach(n => { if (state.get(n.id) === 0) { visit(n.id); } });
	}
	const fwd = edges.filter(e => !back.has(e));

	// ── rank by longest path (Kahn topological order over forward edges) ─────────
	const rank    = new Map<string, number>(nodes.map((n): [string, number]   => [n.id, 0]));
	const indeg   = new Map<string, number>(nodes.map((n): [string, number]   => [n.id, 0]));
	const outFwd  = new Map<string, string[]>(nodes.map((n): [string, string[]] => [n.id, []]));
	const inFwd   = new Map<string, string[]>(nodes.map((n): [string, string[]] => [n.id, []]));
	for (const e of fwd) {
		indeg.set(e.to, indeg.get(e.to)! + 1);
		outFwd.get(e.from)!.push(e.to);
		inFwd.get(e.to)!.push(e.from);
	}
	const queue = nodes.filter(n => indeg.get(n.id) === 0).map(n => n.id);
	let qi = 0;
	while (qi < queue.length) {
		const u = queue[qi++];
		for (const v of outFwd.get(u)!) {
			rank.set(v, Math.max(rank.get(v)!, rank.get(u)! + 1));
			indeg.set(v, indeg.get(v)! - 1);
			if (indeg.get(v) === 0) { queue.push(v); }
		}
	}

	const maxRank = Math.max(0, ...nodes.map(n => rank.get(n.id)!));
	const ranks: string[][] = Array.from({ length: maxRank + 1 }, () => []);
	nodes.forEach(n => ranks[rank.get(n.id)!].push(n.id));

	// ── order within ranks by neighbour barycentre (a few up/down passes) ────────
	const orderPos = new Map<string, number>();
	ranks.forEach(r => r.forEach((id, i) => orderPos.set(id, i)));
	for (let pass = 0; pass < 4; pass++) {
		const forward = pass % 2 === 0;
		const seq = forward ? ranks : [...ranks].reverse();
		for (const r of seq) {
			const bary = new Map<string, number>();
			for (const id of r) {
				const neigh = forward ? inFwd.get(id)! : outFwd.get(id)!;
				const vals  = neigh.map(nid => orderPos.get(nid)!);
				bary.set(id, vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : orderPos.get(id)!);
			}
			r.sort((a, b) => bary.get(a)! - bary.get(b)! || orderPos.get(a)! - orderPos.get(b)!);
			r.forEach((id, i) => orderPos.set(id, i));
		}
	}

	// ── assign coordinates ───────────────────────────────────────────────────────
	const size = new Map<string, { w: number; h: number }>(
		nodes.map((n): [string, { w: number; h: number }] => [n.id, nodeSize(n)]));
	const prim = (id: string) => dir === 'LR' ? size.get(id)!.w : size.get(id)!.h;   // along the flow
	const sec  = (id: string) => dir === 'LR' ? size.get(id)!.h : size.get(id)!.w;   // across the flow

	const rankPrim = ranks.map(r => Math.max(0, ...r.map(prim)));
	const rankSec  = ranks.map(r => r.reduce((s, id, i) => s + sec(id) + (i ? ORDER_GAP : 0), 0));
	const maxSec   = Math.max(0, ...rankSec);

	const rankOffset: number[] = [];
	let acc = PAD;
	for (let r = 0; r < ranks.length; r++) { rankOffset[r] = acc; acc += rankPrim[r] + RANK_GAP; }

	const pos = new Map<string, PositionedNode>();
	ranks.forEach((r, ri) => {
		let cursor = PAD + (maxSec - rankSec[ri]) / 2;   // centre each rank across the flow
		for (const id of r) {
			const { w, h } = size.get(id)!;
			const primPos  = rankOffset[ri] + (rankPrim[ri] - prim(id)) / 2;   // centre in the rank band
			const x = dir === 'LR' ? primPos : cursor;
			const y = dir === 'LR' ? cursor  : primPos;
			const n = nodes[index.get(id)!];
			const cx = x + w / 2, cy = y + h / 2;
			pos.set(id, {
				...n, x, y, w, h, cx, cy,
				points: n.kind === 'decision'
					? `${round(cx)},${round(y)} ${round(x + w)},${round(cy)} ${round(cx)},${round(y + h)} ${round(x)},${round(cy)}`
					: undefined
			});
			cursor += sec(id) + ORDER_GAP;
		}
	});

	const width  = (dir === 'LR' ? acc - RANK_GAP : maxSec + PAD * 2);
	const height = (dir === 'LR' ? maxSec + PAD * 2 : acc - RANK_GAP);

	// ── route edges as smooth Béziers with handles along the flow axis ───────────
	const posEdges: PositionedEdge[] = edges.map(e => {
		const s = pos.get(e.from)!, t = pos.get(e.to)!;
		let p0: number[], p1: number[], p2: number[], p3: number[];
		if (dir === 'LR') {
			p0 = [s.x + s.w, s.cy]; p3 = [t.x, t.cy];
			const mx = (p0[0] + p3[0]) / 2;
			p1 = [mx, p0[1]]; p2 = [mx, p3[1]];
		} else {
			p0 = [s.cx, s.y + s.h]; p3 = [t.cx, t.y];
			const my = (p0[1] + p3[1]) / 2;
			p1 = [p0[0], my]; p2 = [p3[0], my];
		}
		const mid = bezier(0.5, p0, p1, p2, p3);
		return {
			...e, back: back.has(e),
			path: `M ${round(p0[0])} ${round(p0[1])} C ${round(p1[0])} ${round(p1[1])} ${round(p2[0])} ${round(p2[1])} ${round(p3[0])} ${round(p3[1])}`,
			labelX: round(mid[0]), labelY: round(mid[1])
		};
	});

	return { width: Math.ceil(width), height: Math.ceil(height), nodes: [...pos.values()], edges: posEdges };
}
